import classNames from 'classnames';
import snakeCase from 'lodash.snakecase';
import { MouseEventHandler, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useSlotClickEvent from '../../../utils/useSlotClickEvent';
import { showDefaultError } from '../../molecules/Notifications';
import Button from '../Button';
import LoadingIndicator from '../LoadingIndicator';
import ToolTip, { ToolTipProps } from '../ToolTip';
import './style.scss';

export type OptionsOption = {
  key: string;
  label: string;
  defaultIcon?: string;
  activeIcon?: string;
  defaultIcons?: string[];
  labelRight?: string;
  form?: ReactNode;
  onDelete?: () => Promise<void>;
  isDisabled?: boolean;
  additionalInfo?: string;
  shortDescription?: string;
  toolTip?: ToolTipProps;
};

export type OptionProps = {
  option: OptionsOption;
  isSelected: boolean;
  onSelect: (key: string) => void;
  dataTestId: string;
  isDisabled?: boolean;
  isCursorDisabled?: boolean;
  isLoading?: boolean;
};

type IconProps = Partial<OptionsOption> & { isSelected: boolean; isLoading?: boolean };

const Icons = ({ labelRight, defaultIcons, defaultIcon, activeIcon, isSelected, isLoading }: IconProps) => {
  if (isLoading && isSelected) {
    return (
      <div className="option__icon-container">
        <LoadingIndicator isSmall />
      </div>
    );
  } else if (labelRight) {
    return <div className="option__icon-container">{labelRight}</div>;
  } else if (defaultIcons) {
    return (
      <div className="option__icon-container">
        {defaultIcons.map(icon => (
          <img className="option__icon" src={icon} key={icon} />
        ))}
      </div>
    );
  } else if (activeIcon ?? defaultIcon) {
    return <img className="option__icon" src={isSelected && !!activeIcon ? activeIcon : defaultIcon} />;
  } else {
    return null;
  }
};

const Option = ({ option, isSelected, onSelect, dataTestId, isDisabled, isCursorDisabled, isLoading }: OptionProps) => {
  const {
    key,
    label,
    defaultIcon,
    activeIcon,
    defaultIcons,
    labelRight,
    form: Form,
    onDelete,
    additionalInfo,
    shortDescription,
    toolTip
  } = option;
  const [isDeleting, setDeleting] = useState<boolean>(false);
  useSlotClickEvent(`slot_option_${snakeCase(key)}`, () => onSelect(key), isCursorDisabled || isDisabled || isSelected);

  const onClick: MouseEventHandler<HTMLButtonElement> = async e => {
    e.stopPropagation();
    setDeleting(true);
    try {
      await onDelete!();
    } catch (err) {
      showDefaultError();
      setDeleting(false);
      console.error(err);
    }
  };

  return (
    <div
      className={classNames(
        'option-border',
        { 'option-border--selected': isSelected },
        { 'option-border--disabled': isDisabled },
        { 'cursor-disabled': isCursorDisabled }
      )}
      onClick={() => (isDisabled || isCursorDisabled ? null : !isSelected && onSelect(key))}
      data-test-id={dataTestId}
    >
      <div className={classNames({ 'option--container': !!onDelete })}>
        <div className={classNames('option', { 'option--selected': isSelected, 'option--is-button': !!onDelete })}>
          <div className="option__label">
            <div className="option__main-label">
              {label}
              {additionalInfo && <div className="option__additional-info">{additionalInfo}</div>}
            </div>
            {shortDescription && <div className="option__short-description">{shortDescription}</div>}
          </div>
          <div className="option__icons-wrapper">
            <Icons
              isSelected={isSelected}
              labelRight={labelRight}
              defaultIcon={defaultIcon}
              defaultIcons={defaultIcons}
              activeIcon={activeIcon}
              isLoading={isLoading}
            />
            {toolTip && <ToolTip headline={toolTip.headline} text={toolTip.text} />}
          </div>
        </div>
        <slot name={`slot_option_${snakeCase(key)}`} />
        {isSelected && <slot name={`slot_option_selected_${snakeCase(key)}`} />}
        {!!onDelete && (
          <div className="option--button">
            <Button
              isLoading={isDeleting}
              type="button"
              className="option__btn"
              onClick={onClick}
              data-test-id={`delete-recurring-payment-method-${key}`}
            >
              <FormattedMessage id="generic.delete" />
            </Button>
          </div>
        )}
      </div>
      {isSelected && Form && Form}
    </div>
  );
};

export type OptionsProps = {
  value: string;
  onChange: (key: string) => void;
  options: OptionsOption[];
  dataTestIdPrefix: string;
  isLoading: string;
  isVertical?: boolean;
  className?: string;
};

const Options = ({ value, onChange, options, isVertical, dataTestIdPrefix, className, isLoading }: OptionsProps) => {
  const comparitor = (a: OptionsOption, b: OptionsOption) => {
    if (a.isDisabled) {
      return 1;
    } else if (b.isDisabled) {
      return -1;
    } else {
      return 0;
    }
  };

  const sortedOptions = options.sort(comparitor);

  return (
    <div className={classNames('options', className, { 'options--vertical': isVertical })}>
      {sortedOptions.map(option => {
        const isOptionSelected = value === option.key;
        const isOptionLoading = isLoading === option.key;
        return (
          <Option
            key={option.key}
            option={option}
            isSelected={isOptionSelected}
            dataTestId={`${dataTestIdPrefix}-${option.key}`}
            onSelect={onChange}
            isLoading={isOptionLoading}
            isDisabled={option.isDisabled}
            isCursorDisabled={!!isLoading && !isOptionLoading && !isOptionSelected}
          />
        );
      })}
    </div>
  );
};

export default Options;
